import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

/** Учётная запись тенанта (ответ GET /users). */
export interface TenantUser {
  id: string
  email: string
  phone: string | null
  roleId: string
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
  role: { id: string; name: string } | null
}

export interface CreateUserInput {
  email: string
  phone?: string
  password: string
  roleId: string
}

export interface UpdateUserInput {
  email?: string
  phone?: string
  roleId?: string
  isActive?: boolean
}

const USERS_QUERY_KEY = 'tenant-users'

export const useTenantUsers = () =>
  useQuery({
    queryKey: [USERS_QUERY_KEY],
    // Бэкенд отдаёт массив без пагинации
    queryFn: async (): Promise<TenantUser[]> =>
      (await apiClient.getUsers()) as unknown as TenantUser[],
    staleTime: 60 * 1000,
    retry: 1,
  })

export const useCreateTenantUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateUserInput) => apiClient.createUser(data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
}

export const useUpdateTenantUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInput }) =>
      apiClient.updateUser(id, data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
}

export const useDeleteTenantUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteUser(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
}

export const useResetTenantUserPassword = () =>
  useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      apiClient.resetUserPassword(id, password),
  })

/** Текст ошибки из ответа apiClient. */
export function errorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message
    if (Array.isArray(m)) return m.join(', ')
    if (typeof m === 'string' && m) return m
  }
  return fallback
}
